/**
 * What a provider card says about itself: status, summary line, version.
 *
 * @module
 */
import { PROVIDER_STATUS_STYLES } from "./providerAuthCopy";
import { formatProviderProvenanceSummary, isDebugDisclosureEnabled } from "./providerProvenance";
import { ProviderKind, ServerProvider } from "@cmmd-forge/contracts";
import { UnifiedSettings } from "@cmmd-forge/contracts/settings";

export type ProviderSummary =
  | { readonly state: "loading" }
  | { readonly state: "ready"; readonly headline: string; readonly detail: string | null };

export function resolveProviderStatusKey(input: {
  readonly provider: ProviderKind;
  readonly providerEnabled: boolean;
  readonly isComingSoon: boolean;
  readonly liveProvider: ServerProvider | undefined;
  readonly codexAccessMode: UnifiedSettings["providers"]["codex"]["accessMode"];
  readonly hasPersonalCredential?: boolean;
}): keyof typeof PROVIDER_STATUS_STYLES {
  if (!input.providerEnabled) return "disabled";
  if (input.isComingSoon) return "disabled";
  if (input.hasPersonalCredential === true) return "ready";
  if (!input.liveProvider) return "warning";
  if (!input.liveProvider.installed) return "offline";
  if (
    input.liveProvider.auth.status === "authenticated" &&
    (input.liveProvider.status === "ready" || input.liveProvider.status === "warning")
  ) {
    return "ready";
  }
  if (
    input.provider === "codex" &&
    input.codexAccessMode === "workspace" &&
    input.liveProvider.installed &&
    input.liveProvider.status === "ready"
  ) {
    return "ready";
  }
  if (input.liveProvider.auth.status === "unauthenticated") {
    return "offline";
  }
  return input.liveProvider.status in PROVIDER_STATUS_STYLES
    ? (input.liveProvider.status as keyof typeof PROVIDER_STATUS_STYLES)
    : "warning";
}

export function getPublicCodexProviderSummary(input: {
  readonly liveProvider: ServerProvider | undefined;
  readonly accessMode: UnifiedSettings["providers"]["codex"]["accessMode"];
}): ProviderSummary {
  const needsAttention =
    input.liveProvider?.status === "error" || input.liveProvider?.enabled === false;
  return {
    state: "ready",
    headline: needsAttention
      ? "Codex access needs attention"
      : input.liveProvider?.auth.status === "authenticated" && input.accessMode === "personal"
        ? "Using personal ChatGPT access."
        : "Using CMMD workspace access.",
    detail: needsAttention ? "Workspace access or ChatGPT sign-in needs attention." : null,
  };
}

export function resolveProviderCardSummary(input: {
  readonly provider: ProviderKind;
  readonly liveProvider: ServerProvider | undefined;
  readonly isComingSoon: boolean;
  readonly isDebugDisclosureEnabled: boolean;
  readonly codexAccessMode: UnifiedSettings["providers"]["codex"]["accessMode"];
  readonly hasPersonalCredential?: boolean;
}): ProviderSummary {
  if (input.hasPersonalCredential === true) {
    return {
      state: "ready",
      headline:
        input.provider === "claudeAgent"
          ? "Using personal Claude subscription."
          : input.provider === "codex"
            ? "Using personal ChatGPT access."
            : "",
      detail: null,
    };
  }
  if (input.provider === "codex" && !input.isDebugDisclosureEnabled) {
    return getPublicCodexProviderSummary({
      liveProvider: input.liveProvider,
      accessMode: input.codexAccessMode,
    });
  }
  if (input.isComingSoon) {
    return {
      state: "ready",
      headline: "",
      detail: null,
    };
  }
  return getProviderSummary(input.liveProvider);
}

// moved verbatim from the 5377-line SettingsPanels.tsx; complexity is
// unchanged, and fallow reads a relocated function as a new one
// fallow-ignore-next-line complexity
export function getProviderSummary(provider: ServerProvider | undefined): ProviderSummary {
  if (!provider) {
    return { state: "loading" };
  }
  const provenanceSummary = formatProviderProvenanceSummary(provider);
  if (!provider.enabled) {
    return {
      state: "ready",
      headline: ["Unavailable", provenanceSummary].filter(Boolean).join(" · "),
      detail: null,
    };
  }
  if (!provider.installed) {
    // HTTP-endpoint providers (e.g. Apex) report their "needs config" state
    // via `status: "error"` + a human-readable `message`. Surface that as
    // "Configuration required" with a client-friendly prompt; the technical
    // env-var hint inside `provider.message` stays debug-only.
    if (provider.status === "error" && provider.message) {
      return {
        state: "ready",
        headline: "Configuration required",
        detail: isDebugDisclosureEnabled()
          ? provider.message
          : "Ask your Forge administrator to finish setting up this provider.",
      };
    }
    return {
      state: "ready",
      headline: "Unavailable",
      detail: null,
    };
  }
  if (provider.auth.status === "authenticated") {
    // Authenticated providers communicate via the green status dot alone —
    // no redundant "Authenticated" subtitle. Debug mode still surfaces the
    // account label + provenance suffix.
    const authLabel = isDebugDisclosureEnabled()
      ? (provider.auth.label ?? provider.auth.type)
      : undefined;
    const debugLine = [authLabel ? `Authenticated · ${authLabel}` : null, provenanceSummary]
      .filter(Boolean)
      .join(" · ");
    return {
      state: "ready",
      headline: debugLine,
      detail: isDebugDisclosureEnabled() ? (provider.message ?? null) : null,
    };
  }
  if (provider.auth.status === "unauthenticated") {
    return {
      state: "ready",
      headline: "Not connected",
      detail: null,
    };
  }
  if (provider.status === "warning") {
    return {
      state: "ready",
      headline: "Needs attention",
      detail: isDebugDisclosureEnabled() ? (provider.message ?? null) : null,
    };
  }
  if (provider.status === "error") {
    return {
      state: "ready",
      headline: "Unavailable",
      detail: isDebugDisclosureEnabled() ? (provider.message ?? null) : null,
    };
  }
  return {
    state: "ready",
    headline: provenanceSummary ?? "",
    detail: isDebugDisclosureEnabled() ? (provider.message ?? null) : null,
  };
}

export function getProviderVersionLabel(version: string | null | undefined) {
  if (!version) return null;
  return version.startsWith("v") ? version : `v${version}`;
}

// T3-style icon stack: brand-colored provider mark with the status indicator
// overlaid as a small dot on the top-left, ringed by the card background so it
// pops off the logo. While loading, the dot is a neutral pulse instead of an
// alarming amber. Providers without a registered icon fall back to a bare
// status dot (still a neutral skeleton while loading).
